import { useEffect, useState } from 'react';
import { Usd } from '@/utils/CurrencyContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
import { CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      try {
        const response = await api.get(`/payments/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setPaymentInfo(response.data);
          toast.success('Payment successful! You are now enrolled in the course.');
          return;
        }
        
        if (response.data.status === 'expired') {
          setStatus('expired');
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setStatus('timeout');
        }
      } catch (error) {
        setStatus('error');
      }
    };
    
    poll();
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: '#FAF9F6' }} data-testid="payment-success-page">
      <Card className="max-w-md w-full" style={{ borderColor: '#E3E1DB' }}>
        <CardHeader>
          <CardTitle className="text-center" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
            Payment Status
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status === 'checking' && (
            <>
              <Loader2 className="h-16 w-16 animate-spin mx-auto" style={{ color: '#163329' }} />
              <p style={{ color: '#5C635A' }}>Verifying your payment...</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <CheckCircle className="h-16 w-16 mx-auto text-green-600" />
              <h3 className="text-2xl font-bold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                Payment Successful!
              </h3>
              <p style={{ color: '#5C635A' }}>You are now enrolled in the course.</p>
              {paymentInfo && (
                <p className="text-sm" style={{ color: '#5C635A' }}>
                  Amount: {paymentInfo.currency?.toUpperCase() === 'USD' ? <Usd value={paymentInfo.amount} /> : `${paymentInfo.amount} ${paymentInfo.currency.toUpperCase()}`}
                </p>
              )}
              <Button
                onClick={() => navigate('/student/dashboard')}
                className="rounded-full"
                style={{ backgroundColor: '#163329', color: '#FAF9F6' }}
                data-testid="go-to-dashboard-button"
              >
                Go to Dashboard
              </Button>
            </>
          )}
          
          {(status === 'error' || status === 'timeout' || status === 'expired') && (
            <>
              <div className="text-4xl">⚠️</div>
              <h3 className="text-2xl font-bold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                Payment Verification Failed
              </h3>
              <p style={{ color: '#5C635A' }}>Please check your email for payment confirmation or contact support.</p>
              <Button
                onClick={() => navigate('/courses')}
                className="rounded-full"
                style={{ backgroundColor: '#163329', color: '#FAF9F6' }}
              >
                Back to Courses
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;